import { makeAutoObservable } from "mobx";
import { onError } from "src/common/onError";
import { RemoteData } from "src/common/RemoteData";

import { loadCatalogBranch } from "../apiCatalogTree";
import { ZCatalog, ZCatalogClassifierItem } from "../ZCatalog";
import {
  appendCatalogTreeBranch,
  CatalogTreeNode,
  findCatalogTreeNode,
  makeCatalogTreeNodes,
  unassigned,
} from "./CatalogTreeNode";

export class CatalogsTreeStore {
  constructor(public catalog: ZCatalog) {
    makeAutoObservable(this);
    if (catalog.classifiers?.length === 1) {
      this.activateClassifier(catalog.classifiers[0]!.id).catch(onError);
    }
  }

  get classifiers(): ZCatalogClassifierItem[] {
    return this.catalog.classifiers ?? [];
  }

  activeClassifierId: string | null = null;

  setActiveClassifierId(id: string | null) {
    this.activeClassifierId = id;
  }

  async activateClassifier(id: string | null | undefined) {
    this.setSelectedKey(null);
    this.setActiveClassifierId(id ?? null);
    if (!id) {
      this.setRoot({ status: "none" });
    } else {
      try {
        this.setRoot({ status: "wait" });
        const branch = await loadCatalogBranch(this.catalog.id, id);
        const result = makeCatalogTreeNodes(branch);
        this.setRoot({ status: "ready", result });
      } catch (error) {
        this.setRoot({ status: "error", error });
      }
    }
  }

  get classifierDisabled() {
    return this.classifiers.length < 2;
  }

  get classifierPlaceholder() {
    return this.classifiers.length === 0
      ? "Нет классификаторов"
      : "Выберите классификатор";
  }

  root: RemoteData<CatalogTreeNode[]> = { status: "none" };

  setRoot(data: RemoteData<CatalogTreeNode[]>) {
    this.root = data;
  }

  get visualTree(): CatalogTreeNode[] {
    const { root } = this;
    return root.status === "ready" ? root.result : [];
  }

  async loadBranch(node: CatalogTreeNode) {
    const { root, activeClassifierId } = this;
    if (root.status !== "ready" || !activeClassifierId) return;
    const result = [...root.result];
    const parentId = node.id === unassigned ? undefined : node.id;
    const items = await loadCatalogBranch(
      this.catalog.id,
      activeClassifierId,
      parentId,
    );
    appendCatalogTreeBranch(result, node.key, items);
    this.setRoot({ status: "ready", result });
  }

  selectedKey: string | null = null;

  setSelectedKey(key: string | null) {
    this.selectedKey = key;
  }

  get activeNode(): CatalogTreeNode | undefined {
    const { selectedKey, root } = this;
    if (selectedKey === null || root.status !== "ready") return undefined;
    return findCatalogTreeNode(root.result, selectedKey);
  }
}
